import {
  VStack,
  Text,
  Button,
  Image,
  SimpleGrid,
  HStack,
  IconButton,
  Spinner,
  Card,
  CardBody,
  Badge,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { MdCloudUpload, MdClose, MdImage } from 'react-icons/md'
import { FaMicroscope } from 'react-icons/fa'

interface MicroscopeImageUploadProps {
  onImagesUploaded: (files: File[], imageUrls: string[]) => void
  uploadedImages: string[]
  analyzing: boolean
  isReadOnly?: boolean
}

export default function MicroscopeImageUpload({
  onImagesUploaded,
  uploadedImages,
  analyzing,
  isReadOnly = false,
}: MicroscopeImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [storedFiles, setStoredFiles] = useState<File[]>([])

  const handleFileSelect = (files: FileList | null) => {
    if (isReadOnly) return
    if (!files) return

    const newFiles: File[] = []
    const imageUrls: string[] = []

    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        newFiles.push(file)
        const url = URL.createObjectURL(file)
        imageUrls.push(url)
      }
    })

    if (newFiles.length > 0) {
      const allFiles = [...storedFiles, ...newFiles]
      const allUrls = [...uploadedImages, ...imageUrls]
      setStoredFiles(allFiles)
      onImagesUploaded(allFiles, allUrls)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (isReadOnly) {
      setIsDragging(false)
      return
    }
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (isReadOnly) return
    handleFileSelect(e.dataTransfer.files)
  }

  const handleRemoveImage = (index: number) => {
    if (isReadOnly) return
    const newImages = uploadedImages.filter((_, i) => i !== index)
    const newFiles = storedFiles.filter((_, i) => i !== index)
    setStoredFiles(newFiles)
    onImagesUploaded(newFiles, newImages)
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Info Card */}
      <Card bg="purple.50" borderColor="purple.200" borderWidth={1}>
        <CardBody>
          <HStack spacing={2}>
            <FaMicroscope style={{ fontSize: '24px', color: 'var(--chakra-colors-purple-600)' }} />
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="semibold" color="purple.800">
                Analyse de frottis sanguin
              </Text>
              <Text fontSize="xs" color="purple.700">
                Importez vos images de microscope pour démarrer la détection
              </Text>
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Upload Zone */}
      {uploadedImages.length === 0 ? (
        isReadOnly ? (
          <Card borderWidth={1} borderColor="gray.200" bg="gray.50">
            <CardBody>
              <VStack spacing={2} py={6}>
                <MdImage style={{ fontSize: '48px', color: 'var(--chakra-colors-gray-400)' }} />
                <Text fontSize="sm" color="gray.600">
                  Aucune image disponible pour cette analyse.
                </Text>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <Card
            borderWidth={2}
            borderStyle="dashed"
            borderColor={isDragging ? 'purple.500' : 'gray.300'}
            bg={isDragging ? 'purple.50' : 'gray.50'}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            cursor={isReadOnly ? 'not-allowed' : 'pointer'}
            onClick={() => !isReadOnly && fileInputRef.current?.click()}
            transition="all 0.2s"
            _hover={{ borderColor: 'purple.400', bg: 'purple.50' }}
          >
            <CardBody>
              <VStack spacing={4} py={8}>
                <MdCloudUpload
                  style={{ fontSize: '64px', color: 'var(--chakra-colors-purple-500)' }}
                />
                <VStack spacing={2}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Déposez vos images ici ou cliquez pour importer
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    JPG, PNG — images de microscope
                  </Text>
                </VStack>
                <Button colorScheme="purple" size="lg">
                  Sélectionner des images
                </Button>
              </VStack>
            </CardBody>
          </Card>
        )
      ) : (
        <VStack spacing={4} align="stretch">
          {/* Image Grid */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">Images importées</Text>
                  <Badge colorScheme="purple">{uploadedImages.length} image{uploadedImages.length > 1 ? 's' : ''}</Badge>
                </HStack>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  {uploadedImages.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <Image
                        src={url}
                        alt={`Upload ${index + 1}`}
                        borderRadius="md"
                        objectFit="cover"
                        w="100%"
                        h="200px"
                        border="2px solid"
                        borderColor="gray.200"
                      />
                      {!isReadOnly && (
                        <IconButton
                          aria-label="Remove image"
                          icon={<MdClose />}
                          size="sm"
                          colorScheme="red"
                          position="absolute"
                          top={2}
                          right={2}
                          onClick={() => handleRemoveImage(index)}
                          isDisabled={analyzing}
                        />
                      )}
                    </div>
                  ))}
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          {/* Add More Button */}
          {!isReadOnly && (
            <Button
              leftIcon={<MdImage />}
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              colorScheme="purple"
              isDisabled={analyzing}
            >
              Add More Images
            </Button>
          )}

          {/* Analysis Status */}
          {analyzing && (
            <HStack
              p={4}
              bg="purple.50"
              borderRadius="md"
              justify="center"
              spacing={3}
            >
              <Spinner color="purple.500" />
              <VStack spacing={0} align="start">
                <Text color="purple.700" fontWeight="semibold">
                  Analyzing blood smears with AI...
                </Text>
                <Text color="purple.600" fontSize="sm">
                  Using real malaria detection model
                </Text>
              </VStack>
            </HStack>
          )}
        </VStack>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={isReadOnly}
      />
    </VStack>
  )
}
